import * as moment from 'moment';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import FileUpload from '@nkr/components/file-upload';
import useDonorFiles from '@nkr/store/donor/hooks/use-donor-files';
import { styles } from './donor-files-styles';

type TProps = {
    donorId: number,
    documentType: FileDocumentType,
    pendingFile?: File,
    onFileSelected: (file: File) => void,
    onFileRemoved: () => void,
    onAppendChanged: (append: boolean) => void,
}

export default function DonorFiles({ donorId, documentType, pendingFile, onFileSelected, onFileRemoved, onAppendChanged }: TProps) {
    const { error, list, download } = useDonorFiles(donorId, documentType);
    const [appendToExisting, setAppendToExisting] = useState(false);

    const handleFileDrop = (acceptedFiles: File[]) => {
        onFileSelected(acceptedFiles[0]);
    };

    const typeName = documentType == FileDocumentType.DonorAdp ? 'Consent Form' : 'File';
    const hasFiles = list.length > 0 || pendingFile != null;

    return (
        <div>
            <Row>
                <Col xs={12} lg={6}>
                    {!hasFiles
                        ? (
                            <Row>
                                <Col xs={12} lg={6} style={styles.uploadLabel}>
                                    <label>{`${typeName}(s):`}</label>
                                </Col>
                                <Col xs={12} lg={6}>
                                    <span className='form-control-static'>N/A</span>
                                </Col>
                            </Row>
                        )
                        : (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>{`${typeName}(s):`}</th>
                                        <th style={styles.th}>Uploaded By</th>
                                        <th style={styles.th}>Upload Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map(file => (
                                        <tr key={file.fileId}>
                                            <td style={styles.td}>
                                                <a style={styles.fileLink} onClick={() => download(file)}>
                                                    {file.fileName}
                                                </a>
                                            </td>
                                            <td style={styles.td}>
                                                {file.uploadedByName ?? 'E-Sign Completed'}
                                            </td>
                                            <td style={styles.td}>
                                                {file.uploadedOn ? moment(file.uploadedOn).format('M/D/YYYY') : 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                    {pendingFile && (
                                        <tr>
                                            <td style={styles.td}>
                                                <em className='text-muted'>{pendingFile.name} (pending save)</em>
                                            </td>
                                            <td style={styles.td}></td>
                                            <td style={styles.td}></td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )
                    }
                </Col>
            </Row>
            <Row>
                <Col xs={12} lg={3} style={styles.uploadLabel}>
                    <label>{`Upload ${typeName}:`}</label>
                </Col>
                <Col xs={12} lg={9}>
                    <div style={styles.uploadRow}>
                        {list.length > 0 && pendingFile && (
                            <div style={styles.appendRow}>
                                <span>Append this upload to the existing file?</span>
                                <label className='radio-inline'>
                                    <input type='radio' checked={appendToExisting} onClick={() => { setAppendToExisting(true); onAppendChanged(true); }} /> Yes
                                </label>
                                <label className='radio-inline'>
                                    <input type='radio' checked={!appendToExisting} onClick={() => { setAppendToExisting(false); onAppendChanged(false); }} /> No
                                </label>
                            </div>
                        )}
                        <div style={list.length > 0 && pendingFile ? styles.uploadShrink : styles.uploadFull}>
                            <FileUpload
                                label='Click here to select the file or drag &amp; drop the file here...'
                                files={pendingFile != null ? [pendingFile] : []}
                                height='30px'
                                accept='.pdf'
                                onFileDropped={handleFileDrop}
                                singleUploadOnly={true}
                                onRemoveFile={onFileRemoved}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
            {error && (
                <Row>
                    <Col xs={12}>
                        <span className='text-danger'>{error}</span>
                    </Col>
                </Row>
            )}
        </div>
    );
}
